import LoadError from "../components/LoadError";
import { AlertCircle, Calendar, FileText, Plus, RefreshCw } from "lucide-react";
import { Link } from "react-router-dom";
import React, { useEffect, useState } from "react";
import { abonnementsAPI, facturesAPI } from "../services/api";
import InvoiceList from "../components/InvoiceList";
import { formatMoney } from "../utils/format";

// Le backend renvoie les factures dans l'ordre d'insertion : on remet la plus
// récente en tête pour l'affichage.
const sortFacturesRecentesDabord = (list) =>
  [...list].sort((a, b) => {
    const dateA = new Date(a.date_creation).getTime();
    const dateB = new Date(b.date_creation).getTime();
    const validA = Number.isFinite(dateA);
    const validB = Number.isFinite(dateB);

    if (validA && validB && dateA !== dateB) return dateB - dateA;
    // Une facture sans date exploitable part en fin de liste.
    if (validA !== validB) return validA ? -1 : 1;
    return Number(b.id || 0) - Number(a.id || 0);
  });

const Dashboard = ({ invoicesOnly = false }) => {
  const [factures, setFactures] = useState([]);
  const [loadError, setLoadError] = useState("");
  const [loading, setLoading] = useState(true);
  const [echeances, setEcheances] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState("all");

  useEffect(() => {
    loadFactures();
    loadEcheances();
  }, []);

  const loadEcheances = async () => {
    try {
      const response = await abonnementsAPI.getEcheances();
      setEcheances(response.data);
    } catch (error) {
      // L'encart des abonnements est secondaire : ne pas polluer le dashboard.
      setEcheances(null);
    }
  };

  const loadFactures = async () => {
    setLoadError("");
    try {
      const response = await facturesAPI.getAll();
      setFactures(sortFacturesRecentesDabord(response.data));
    } catch (error) {
      setLoadError("Impossible de charger les factures. Les données précédentes sont conservées.");
    } finally {
      setLoading(false);
    }
  };

  const parseAmount = (value) => {
    const amount = Number(value);
    return Number.isFinite(amount) ? amount : 0;
  };

  const getFactureTotalEur = (facture) => {
    const totalHt = parseAmount(facture.total_ht);
    const devise = (facture.client_devise || "EUR").toUpperCase();

    if (devise === "EUR") return totalHt;

    const rate = Number(facture.fx_rate_eur_per_devise);
    if (!Number.isFinite(rate) || rate <= 0) return null;
    return totalHt * rate;
  };


  const currentYear = new Date().getFullYear();
  const availableYears = [...new Set([
    currentYear,
    ...factures
      .filter((facture) => facture.date_creation)
      .map((facture) => new Date(facture.date_creation).getFullYear())
      .filter((year) => Number.isFinite(year)),
  ])].sort((a, b) => b - a);

  const facturesPeriode = factures.filter((facture) => {
    if (!facture.date_creation) return false;
    const date = new Date(facture.date_creation);
    return (
      !Number.isNaN(date.getTime()) &&
      date.getFullYear() === selectedYear &&
      (selectedMonth === "all" || date.getMonth() === Number(selectedMonth))
    );
  });

  const facturesPayeesPeriode = facturesPeriode.filter(
    (facture) => facture.statut === "paye"
  );

  const totalFactures = facturesPeriode.length;
  const facturesPayees = facturesPayeesPeriode.length;

  const caPayeParDevise = facturesPayeesPeriode.reduce((acc, facture) => {
    const devise = facture.client_devise || "EUR";
    if (!acc[devise]) {
      acc[devise] = 0;
    }
    acc[devise] += parseAmount(facture.total_ht);
    return acc;
  }, {});

  const caPayeEur = facturesPayeesPeriode.reduce((sum, facture) => {
    const totalEur = getFactureTotalEur(facture);
    return totalEur === null ? sum : sum + totalEur;
  }, 0);

  const facturesAVenirPeriode = facturesPeriode.filter(
    (facture) => facture.statut === "non_paye"
  );

  const caAVenirParDevise = facturesAVenirPeriode.reduce((acc, facture) => {
    const devise = facture.client_devise || "EUR";
    if (!acc[devise]) {
      acc[devise] = 0;
    }
    acc[devise] += parseAmount(facture.total_ht);
    return acc;
  }, {});

  const caAVenirEur = facturesAVenirPeriode.reduce((sum, facture) => {
    const totalEur = getFactureTotalEur(facture);
    return totalEur === null ? sum : sum + totalEur;
  }, 0);

  const monthLabels = [
    "Janvier",
    "Février",
    "Mars",
    "Avril",
    "Mai",
    "Juin",
    "Juillet",
    "Août",
    "Septembre",
    "Octobre",
    "Novembre",
    "Décembre",
  ];

  const facturesPayeesAnnee = factures.filter((facture) => {
    if (!facture.date_creation) return false;
    const date = new Date(facture.date_creation);
    return (
      !Number.isNaN(date.getTime()) &&
      date.getFullYear() === selectedYear &&
      facture.statut === "paye"
    );
  });

  const caPayeEurParMois = facturesPayeesAnnee.reduce((acc, facture) => {
    const totalEur = getFactureTotalEur(facture);
    if (totalEur === null) return acc;

    const invoiceDate = new Date(facture.date_creation);
    if (Number.isNaN(invoiceDate.getTime())) return acc;

    const monthIndex = invoiceDate.getMonth();
    acc[monthIndex] = (acc[monthIndex] || 0) + totalEur;
    return acc;
  }, {});

  const monthlyPaidEur = monthLabels.map((month, index) => ({
    month,
    index,
    amount: caPayeEurParMois[index] || 0,
  }));
  const annualPaidEur = monthlyPaidEur.reduce(
    (sum, item) => sum + item.amount,
    0
  );
  const periodLabel =
    selectedMonth === "all"
      ? String(selectedYear)
      : `${monthLabels[Number(selectedMonth)]} ${selectedYear}`;

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{invoicesOnly ? "Factures" : "Tableau de bord"}</h1>
          <p className="text-gray-600">{invoicesOnly ? "Retrouvez vos documents et suivez les paiements" : "Votre activité et les documents à traiter"}</p>
        </div>
        <div className="flex flex-col gap-3 sm:flex-row">
          <Link
            to="/factures/nouvelle"
            className="btn-primary flex w-full items-center justify-center gap-2 sm:w-auto"
          >
            <Plus className="h-5 w-5" />
            <span>Nouvelle facture</span>
          </Link>
        </div>
      </div>

      {/* Abonnements récurrents arrivés à échéance */}
      {!invoicesOnly && echeances?.total_periodes > 0 && (
        <div className="rounded-lg border-l-4 border-amber-500 bg-amber-50 p-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-3">
              <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
              <div>
                <p className="font-semibold text-amber-900">
                  {echeances.total_periodes} période(s) d'abonnement à facturer
                </p>
                <p className="text-sm text-amber-800">
                  {echeances.abonnements
                    .slice(0, 3)
                    .map(
                      (item) =>
                        `${item.libelle} (${item.client_nom || "client supprimé"})`
                    )
                    .join(", ")}
                  {echeances.total_abonnements > 3
                    ? ` et ${echeances.total_abonnements - 3} autre(s)`
                    : ""}
                </p>
              </div>
            </div>
            <Link
              to="/abonnements"
              className="btn-primary flex items-center justify-center gap-2 sm:w-auto"
            >
              <RefreshCw className="h-4 w-4" />
              <span>Voir les abonnements</span>
            </Link>
          </div>
        </div>
      )}

      {!invoicesOnly && <>
      <div className="grid gap-3 sm:grid-cols-2">
        <Link to="/factures?statut=non_paye" className="rounded-xl border border-primary-100 bg-primary-50 p-4"><p className="font-semibold text-primary-900">{factures.filter((f) => f.statut === "non_paye").length} facture(s) à encaisser</p><p className="text-sm text-primary-700">Consulter les échéances et les paiements</p></Link>
        <Link to="/factures?statut=suivi" className="rounded-xl border border-amber-100 bg-amber-50 p-4"><p className="font-semibold text-amber-900">{factures.filter((f) => f.relances?.a_traiter || f.envoi_initial?.a_verifier || f.envoi_initial?.etat === "echec").length} document(s) à suivre</p><p className="text-sm text-amber-800">Vérifier les envois et les relances</p></Link>
      </div>
      <div className="card flex flex-col gap-4 sm:flex-row sm:items-end">
        <div>
          <label
            htmlFor="ca-year"
            className="mb-1 block text-sm font-medium text-gray-700"
          >
            Année d’émission
          </label>
          <select
            id="ca-year"
            value={selectedYear}
            onChange={(event) => setSelectedYear(Number(event.target.value))}
            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 sm:w-40"
          >
            {availableYears.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label
            htmlFor="ca-month"
            className="mb-1 block text-sm font-medium text-gray-700"
          >
            Mois d’émission
          </label>
          <select
            id="ca-month"
            value={selectedMonth}
            onChange={(event) => setSelectedMonth(event.target.value)}
            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 sm:w-48"
          >
            <option value="all">Toute l'année</option>
            {monthLabels.map((month, index) => (
              <option key={month} value={index}>
                {month}
              </option>
            ))}
          </select>
        </div>
      </div>

      <p className="text-xs text-gray-500">Période d’émission des factures, montants HT. Les totaux sont convertis en EUR avec le taux enregistré sur chaque facture.</p>
      {/* Stats */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-4">
        <div className="card">
          <div className="flex items-start gap-4">
            <div className="p-2 bg-blue-100 rounded-lg">
              <FileText className="h-6 w-6 text-blue-600" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-600">
                Factures de la période
              </p>
              <p className="text-2xl font-bold text-gray-900">
                {totalFactures}
              </p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-start gap-4">
            <div className="p-2 bg-green-100 rounded-lg">
              <FileText className="h-6 w-6 text-green-600" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-gray-600">
                Factures payées {periodLabel}
              </p>
              <p className="text-xl font-bold text-gray-900">
                {formatMoney(caPayeEur)}
              </p>
              {Object.keys(caPayeParDevise).length === 0 ? (
                <p className="text-xs text-gray-500 mt-1">
                  Aucun CA payé en devise source
                </p>
              ) : Object.keys(caPayeParDevise).length === 1 && caPayeParDevise.EUR !== undefined ? null : (
                <div className="space-y-1 mt-1">
                  <p className="text-xs text-gray-500">Montants par devise</p>
                  {Object.entries(caPayeParDevise).map(([devise, montant]) => (
                    <p
                      key={devise}
                      className="text-sm font-semibold text-gray-700"
                    >
                      {formatMoney(montant, devise)}
                    </p>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-start gap-4">
            <div className="p-2 bg-orange-100 rounded-lg">
              <Calendar className="h-6 w-6 text-orange-600" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-gray-600">
                À encaisser {periodLabel}
              </p>
              <p className="text-xl font-bold text-gray-900">
                {formatMoney(caAVenirEur)}
              </p>
              {Object.keys(caAVenirParDevise).length === 0 ? (
                <p className="text-xs text-gray-500 mt-1">
                  Aucun CA à venir en devise source
                </p>
              ) : Object.keys(caAVenirParDevise).length === 1 && caAVenirParDevise.EUR !== undefined ? null : (
                <div className="space-y-1 mt-1">
                  <p className="text-xs text-gray-500">Montants par devise</p>
                  {Object.entries(caAVenirParDevise).map(
                    ([devise, montant]) => (
                      <p
                        key={devise}
                        className="text-sm font-semibold text-gray-700"
                      >
                        {formatMoney(montant, devise)}
                      </p>
                    )
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-start gap-4">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <Calendar className="h-6 w-6 text-yellow-600" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-600">
                Payées sur la période
              </p>
              <p className="text-2xl font-bold text-gray-900">
                {facturesPayees}/{totalFactures}
              </p>
            </div>
          </div>
        </div>
      </div>

      <details className="card">
        <summary className="cursor-pointer font-semibold">Voir le détail mensuel {selectedYear}</summary>
        <div className="mb-4 mt-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <h2 className="text-lg font-semibold text-gray-900">
            Détail mensuel {selectedYear}
          </h2>
          <span className="text-sm font-semibold text-gray-700">
            Total annuel: {formatMoney(annualPaidEur)}
          </span>
        </div>
        {annualPaidEur === 0 && (
          <p className="text-sm text-gray-500">
            Aucune facture payée avec équivalent EUR sur {selectedYear}.
          </p>
        )}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {monthlyPaidEur.map((item) => (
            <div
              key={item.month}
              className={`rounded-lg border p-3 ${selectedMonth === String(item.index) ? "border-primary-600 bg-primary-50" : ""}`}
            >
              <p className="text-xs uppercase text-gray-500">{item.month}</p>
              <p className="text-base font-semibold text-gray-900">
                {formatMoney(item.amount)}
              </p>
            </div>
          ))}
        </div>
      </details>

      </>}
      <LoadError message={loadError} onRetry={loadFactures} />
      {(!loadError || factures.length > 0) && <InvoiceList factures={factures} onChanged={loadFactures} recent={!invoicesOnly} />}
    </div>
  );
};

export default Dashboard;
